import {addDays, compareAsc, differenceInCalendarDays, isValid, subDays} from 'date-fns';
import {match} from 'ts-pattern';

import {clamp, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {MAX_DUE_DAYS} from '../constants/MAX_DUE_DAYS';
import {MIN_DUE_DAYS} from '../constants/MIN_DUE_DAYS';

export interface PaymentDates {
  issueDate: Date | Nullish;
  dueDays: number | Nullish;
  dueDate: Date | Nullish;
  taxableSupplyDate: Date | Nullish;
}

export type PaymentDatesField = keyof PaymentDates;

export interface ResolvePaymentDatesOptions {
  /**
   * When set, a changed issue date keeps the taxable supply date within
   * `issueDate - maxTaxableSupplyDaysBeforeIssue` and `issueDate`.
   */
  maxTaxableSupplyDaysBeforeIssue?: number;
}

const toValidDate = (date: Date | Nullish) => (isNotNil(date) && isValid(date) ? date : null);

const clampDueDays = clamp(MIN_DUE_DAYS, MAX_DUE_DAYS);

const resolveTaxableSupplyDate = (
  issueDate: Date,
  taxableSupplyDate: Date | null,
  maxDaysBeforeIssue: number
) => {
  const minDate = subDays(issueDate, maxDaysBeforeIssue);

  return match(taxableSupplyDate)
    .when(isNil, () => issueDate)
    .when(
      (date) => compareAsc(date, minDate) < 0,
      () => minDate
    )
    .when(
      (date) => compareAsc(date, issueDate) > 0,
      () => issueDate
    )
    .otherwise((date) => date);
};

const resolveOnIssueDateChange = (values: PaymentDates, options: ResolvePaymentDatesOptions) => {
  const issueDate = toValidDate(values.issueDate);

  if (isNil(issueDate)) {
    return {};
  }

  const dueDays = isNotNil(values.dueDays) ? clampDueDays(values.dueDays) : null;
  const dueDaysPatch = isNotNil(dueDays) && dueDays !== values.dueDays ? {dueDays} : {};
  const dueDatePatch = isNotNil(dueDays) ? {dueDate: addDays(issueDate, dueDays)} : {};
  const taxableSupplyPatch = isNotNil(options.maxTaxableSupplyDaysBeforeIssue)
    ? {
        taxableSupplyDate: resolveTaxableSupplyDate(
          issueDate,
          toValidDate(values.taxableSupplyDate),
          options.maxTaxableSupplyDaysBeforeIssue
        ),
      }
    : {};

  return {...dueDaysPatch, ...dueDatePatch, ...taxableSupplyPatch};
};

const resolveOnDueDaysChange = (values: PaymentDates) => {
  if (isNil(values.dueDays)) {
    return {};
  }

  const dueDays = clampDueDays(values.dueDays);
  const issueDate = toValidDate(values.issueDate);
  const dueDaysPatch = dueDays !== values.dueDays ? {dueDays} : {};
  const dueDatePatch = isNotNil(issueDate) ? {dueDate: addDays(issueDate, dueDays)} : {};

  return {...dueDaysPatch, ...dueDatePatch};
};

const resolveOnDueDateChange = (values: PaymentDates) => {
  const issueDate = toValidDate(values.issueDate);
  const dueDate = toValidDate(values.dueDate);

  if (isNil(issueDate) || isNil(dueDate)) {
    return {};
  }

  const dueDays = differenceInCalendarDays(dueDate, issueDate);
  const clampedDueDays = clampDueDays(dueDays);

  return clampedDueDays === dueDays
    ? {dueDays}
    : {dueDays: clampedDueDays, dueDate: addDays(issueDate, clampedDueDays)};
};

/**
 * Resolves the payment dates that follow from a change of one of them.
 * The issue date is the anchor: it changes only when the user edits it.
 *
 * @param changedField - The field the user has edited.
 * @param values - All payment dates, with the new value of `changedField` already applied.
 * @param options - Form-specific rules.
 * @returns Only the fields that must be updated in the form.
 */
export const resolvePaymentDates = (
  changedField: PaymentDatesField,
  values: PaymentDates,
  options: ResolvePaymentDatesOptions = {}
): Partial<PaymentDates> =>
  match(changedField)
    .with('issueDate', () => resolveOnIssueDateChange(values, options))
    .with('dueDays', () => resolveOnDueDaysChange(values))
    .with('dueDate', () => resolveOnDueDateChange(values))
    .with('taxableSupplyDate', () => ({}))
    .exhaustive();
